// Dedicated landing pages for the four service intents the Google Ads campaign
// bids on (removal, storm/emergency, trimming, stump). Ads will point themed
// ad groups at these URLs so someone searching "fallen tree removal" lands on
// a page about exactly that, not the generic homepage — that mismatch is what
// Google was scoring as poor landing page experience.
//
// Same rule as the town pages: every page stands on its own. Copy describes
// how the work is actually done and what drives cost; nothing claims specific
// past jobs or response-time guarantees we can't keep. Geography stays at the
// county level.

export const SERVICE_PAGES = [
  {
    slug: 'tree-removal',
    name: 'Tree Removal',
    quoteService: 'Tree Removal',
    emergency: false,
    metaTitle: 'Tree Removal in Maryland | Free Estimates | One Choice Tree Service',
    metaDescription:
      'Safe tree removal across Anne Arundel, Howard, Baltimore & Carroll counties, MD. Sectional takedowns, crane work, full cleanup. Licensed & insured. Free estimates: (443) 208-0747.',
    priority: '0.9',
    heroLine: 'Tree Removal in Maryland',
    intro:
      "Dead, storm-damaged, or just too close to the house — when a tree has to come down, how it comes down is everything. We take trees apart in sections, rig the pieces down under control, and leave the yard the way we found it, minus the tree.",
    notes: [
      {
        title: 'Taken down in sections, not felled',
        body: "Most Maryland yards don't have room to drop a tree in one piece, and we don't try. A climber or lift takes the crown apart from the top, and every section is roped or craned down to a controlled landing zone — nothing swings free over the roof, the fence, or the neighbor's side."
      },
      {
        title: 'What actually sets the price',
        body: "Size, access, and condition. A tall tree a chip truck can park next to costs less than a medium one locked behind a pool and a fence. Dead trees cost more than living ones — brittle wood can't be climbed or rigged the same way — which is the honest argument for removing a dying tree before it finishes dying."
      },
      {
        title: 'The property line and the street',
        body: "Trees in the public right-of-way fall under Maryland's Roadside Tree Law and need a state permit before anyone touches them, and a boundary-line tree is a conversation with the neighbor before it's a job. We sort out which situation you have during the assessment, not after."
      }
    ],
    faqs: [
      {
        q: 'How much does tree removal cost?',
        a: "There's no honest flat number — a small ornamental and a 90-foot oak over a house are different jobs. Size, access for equipment, and the tree's condition drive the price. The estimate is free, on site, and itemized, so you see what you're paying for before anything is scheduled."
      },
      {
        q: 'Does removal include the stump?',
        a: "Stump grinding is quoted as its own line item so you can decide either way. Most people have us grind it in the same visit — the equipment is already there and it's cheaper than a separate trip."
      },
      {
        q: 'Do I need a permit to remove a tree in my yard?',
        a: "For a tree fully on private residential property, usually no in the counties we serve — but trees in the public right-of-way need a state permit under Maryland's Roadside Tree Law, and some HOAs have their own rules. We flag anything that applies when we quote the job."
      },
      {
        q: 'Will homeowners insurance pay for removal?',
        a: "Generally only when the tree has already fallen and damaged a covered structure. Removing a standing tree — even a hazardous one — is normally the homeowner's cost. That's worth knowing before a leaning tree becomes an insurance claim with a deductible."
      }
    ],
    related: ['emergency-tree-service', 'stump-grinding', 'tree-trimming']
  },
  {
    slug: 'emergency-tree-service',
    name: 'Emergency Tree Service',
    quoteService: 'Storm Damage / Emergency',
    emergency: true,
    metaTitle: '24/7 Emergency Tree Service Maryland | Storm Damage | One Choice',
    metaDescription:
      'Tree on your house? 24/7 emergency tree removal and storm damage response in Anne Arundel, Howard, Baltimore & Carroll counties, MD. Call now: (443) 208-0747.',
    priority: '0.9',
    heroLine: '24/7 Emergency Tree Service',
    intro:
      "A tree on the house, a limb through the roof, a leaner that moved in the last storm — these don't wait for business hours and neither do we. Call, tell us what happened, and we'll tell you what happens next.",
    notes: [
      {
        title: 'Tree on the house? Make-safe comes first',
        body: "The first visit is about stopping the damage from getting worse: lifting the weight off the structure, cutting away what's pressing on it, and tarping the opening. The full removal and cleanup can follow in daylight — the emergency part is making the building safe tonight."
      },
      {
        title: 'Hangers and leaners count too',
        body: "A broken limb caught in the canopy over a driveway, or a tree whose root plate lifted in the wind, hasn't finished falling — it's paused. Those are emergencies before the next front comes through, not after, and they're exactly the calls we'd rather get early."
      },
      {
        title: 'Documented for your insurance claim',
        body: "Storm damage to a structure is usually a homeowners claim. We photograph the scene before we touch it, keep the work itemized, and write the invoice so your adjuster can follow what was done and why. You handle the claim; we make it easy to file."
      }
    ],
    faqs: [
      {
        q: 'How fast can you get here?',
        a: "Call and we'll give you a real answer for right now — it depends on where you are and what the storm did to the rest of the county. Trees on houses and blocked driveways come first; a tree flat in the yard that isn't touching anything can safely wait its turn."
      },
      {
        q: 'Does insurance cover emergency tree work?',
        a: "When a tree damages a covered structure, homeowners policies generally cover the removal from the structure and the repair — minus your deductible. A tree that fell in the open yard usually isn't covered. Either way you'll get documentation that holds up with an adjuster."
      },
      {
        q: 'There’s a tree on a power line. Now what?',
        a: "Stay away from it and call your utility first — a tree touching a line is their emergency, not ours, and no tree crew should touch it until the line is confirmed dead or cleared. Once the utility has done its part, we handle the tree."
      },
      {
        q: 'What should I do while I wait?',
        a: "Keep people and pets away from the tree — especially under anything hanging. Don't start cutting; wood under storm load stores tension in ways that hurt people. If it's safe to do so, photos from a distance help your claim. Then leave it alone until we get there."
      }
    ],
    related: ['tree-removal', 'tree-trimming', 'stump-grinding']
  },
  {
    slug: 'tree-trimming',
    name: 'Tree Trimming & Pruning',
    quoteService: 'Tree Trimming & Pruning',
    emergency: false,
    metaTitle: 'Tree Trimming & Pruning in Maryland | Free Estimates | One Choice',
    metaDescription:
      'Professional tree trimming, crown reduction and deadwood removal in Anne Arundel, Howard, Baltimore & Carroll counties, MD. Licensed & insured. Free estimates: (443) 208-0747.',
    priority: '0.8',
    heroLine: 'Tree Trimming & Pruning in Maryland',
    intro:
      "Good pruning is the cheapest tree work you'll ever buy — it's what keeps the expensive calls from happening. We trim for clearance, weight, and structure so the tree stays healthy and the parts over your roof stay attached.",
    notes: [
      {
        title: 'Reduction, not topping',
        body: "Topping — lopping a crown flat — ruins trees. The regrowth is weakly attached, comes back fast, and fails in storms. A proper crown reduction cuts back to sound side branches so the tree ends up smaller, balanced, and stronger, not butchered and hazardous."
      },
      {
        title: 'Clearance and deadwood are most of the work',
        body: "Limbs off the roofline, out of the service drop, away from the chimney — and the standing deadwood cut out before it picks its own moment to drop. Those two things are the bulk of what mature shade trees in this region actually need."
      },
      {
        title: 'When to do it',
        body: "Structural pruning is easiest in the dormant season, when the branch architecture is visible and the tree seals cuts well in spring. But a limb bearing on the roof or cracked in a storm is a now job in any season — waiting for winter is for work that can wait."
      }
    ],
    faqs: [
      {
        q: 'How often should trees be trimmed?',
        a: "A mature shade tree near a house is typically on a three-to-five year cycle. Fast growers over structures run shorter; a healthy tree standing in the open can go much longer. We'll tell you honestly if yours doesn't need work yet."
      },
      {
        q: 'Can you take just the one limb hanging over my roof?',
        a: "Yes — small, targeted jobs are normal work, not a nuisance. While we're up there we'll tell you if anything else in the crown is a problem, but nobody will talk you into work the tree doesn't need."
      },
      {
        q: 'Do you top trees?',
        a: "No. Topping creates the exact hazard people think it removes — dense, weakly attached regrowth that fails in wind. If a tree is genuinely too big for where it stands, the honest options are a proper crown reduction or removal, and we'll lay out both."
      },
      {
        q: 'Will trimming stress the tree?',
        a: "Done right, no — pruning to industry standard means cutting at the right points and never removing more of the live crown than the tree can afford in one season. That restraint is the difference between pruning and damage."
      }
    ],
    related: ['tree-removal', 'emergency-tree-service', 'stump-grinding']
  },
  {
    slug: 'stump-grinding',
    name: 'Stump Grinding',
    quoteService: 'Stump Grinding',
    emergency: false,
    metaTitle: 'Stump Grinding in Maryland | Free Estimates | One Choice Tree Service',
    metaDescription:
      'Stump grinding and removal in Anne Arundel, Howard, Baltimore & Carroll counties, MD. Ground below grade, ready to seed or replant. Licensed & insured. Free quotes: (443) 208-0747.',
    priority: '0.8',
    heroLine: 'Stump Grinding in Maryland',
    intro:
      "The tree's gone but the stump is still there — sprouting, dulling mower blades, and holding the spot hostage. Grinding takes it out below grade in an hour or two, and the lawn gets its ground back.",
    notes: [
      {
        title: 'Ground out below grade',
        body: "The grinder chews the stump into chips to six to twelve inches below the surface — enough to seed grass over the spot like the tree was never there. Planning to replant or run a fence post through it? Say so, and we grind deeper."
      },
      {
        title: 'Utilities get located first',
        body: "Grinder teeth and a buried gas or cable line must never meet. Anywhere there's a chance of utilities near the stump, marking comes before grinding — it's a legal requirement in Maryland, not a formality, and it's on us to respect it."
      },
      {
        title: "What's left when we're done",
        body: "Grinding produces a surprising mound of wood chips. Standard is to backfill the hole with them and level it off — they settle as they break down. If you'd rather have them hauled off and the hole topped with soil, that's a small add-on we'll quote up front."
      }
    ],
    faqs: [
      {
        q: 'How deep do you grind?',
        a: "Six to twelve inches below grade as standard, which handles grass, mulch beds, and most landscaping. Deeper grinds for replanting or construction are available — tell us what's going in the spot and we'll grind to suit it."
      },
      {
        q: 'Can I plant a new tree in the same spot?',
        a: "Yes, with a deeper grind and fresh soil brought in — though a new tree usually establishes faster planted a few feet to the side, outside the old root mass. We'll tell you which your spot calls for."
      },
      {
        q: 'What happens to the roots?',
        a: "Grinding removes the stump itself; the outlying roots stay and simply decay in the ground over a few years. They don't keep growing once the stump is gone — the sprouts you see around old stumps come from the stump being left, which is what grinding prevents."
      },
      {
        q: 'Do you have to be the ones who removed the tree?',
        a: "Not at all. Old stumps, storm losses someone else cleaned up, stumps that came with the house — the grinder doesn't care who cut the tree. Send a photo with something for scale and we can usually quote it from that."
      }
    ],
    related: ['tree-removal', 'tree-trimming', 'emergency-tree-service']
  }
];

export const getServicePage = slug => SERVICE_PAGES.find(page => page.slug === slug);
